import { z } from 'zod';
import { isoDate } from './booking.js';
import { optionalHexColorSchema, tagListSchema } from './tags.js';
/* ═══════════════════════════════════════════════════════════════════════
 * CLIENTS — Diana's own students, and what they have paid for.
 *
 * A booking from the public page is a stranger with an e-mail address. A
 * client is someone Diana teaches: she adds them here, tags them (so that
 * "premium" and "low" rates can be told apart and filtered), records what
 * they bought, and puts their lessons into the calendar herself.
 *
 * Same three-schema rule as content.ts: base → input (defaults) → patch (no
 * defaults). A patch with defaults would wipe the tags on every rename.
 * ═══════════════════════════════════════════════════════════════════════ */
const clientBase = z.object({
    firstName: z.string().trim().min(1, 'Podaj imię').max(120),
    lastName: z.string().trim().max(120),
    email: z.string().trim().max(200),
    phone: z.string().trim().max(40),
    /**
     * Lower-cased labels: "premium", "low", "speak and roll". They filter the
     * books and the client list, and the FIRST one carrying a colour paints
     * this student's lessons — so the order she typed them in matters.
     */
    tags: tagListSchema,
    notes: z.string().max(4000),
    /** Her usual per-lesson price for this person, in grosze. */
    defaultPriceGrosz: z.number().int().nonnegative(),
    /** Inactive students stay in the books but drop out of the dropdowns. */
    active: z.boolean(),
    /** Overrides the tag colour in the calendar. '' = use the first coloured tag. */
    color: optionalHexColorSchema,
});
export const clientInputSchema = clientBase.extend({
    lastName: clientBase.shape.lastName.default(''),
    email: clientBase.shape.email.default(''),
    phone: clientBase.shape.phone.default(''),
    tags: clientBase.shape.tags.default([]),
    notes: clientBase.shape.notes.default(''),
    defaultPriceGrosz: clientBase.shape.defaultPriceGrosz.default(0),
    active: clientBase.shape.active.default(true),
    color: clientBase.shape.color.default(''),
});
/** No defaults — an absent key means "leave it alone". */
export const clientPatchSchema = clientBase.partial();
export const clientSchema = clientInputSchema.extend({
    id: z.uuid(),
    createdAt: z.coerce.date(),
    /**
     * Their login, once Diana has invited them to the platform. Read-only here
     * on purpose: it is deliberately absent from `clientBase`, so neither the
     * create form nor a patch can set it. Linking an account is its own
     * endpoint, because it is an authorisation change and not an edit.
     */
    userId: z.string().nullable().default(null),
});
/* ── Purchases ─────────────────────────────────────────────────────────
 * "Bought 10 lessons for 1100 zł" or "paid for one lesson, 120 zł". The
 * per-lesson price is derived (total / lessons) and never stored twice.
 */
export const PURCHASE_KINDS = ['single', 'package'];
export const PURCHASE_KIND_LABELS = {
    single: 'Pojedyncze lekcje',
    package: 'Pakiet',
};
export const purchaseInputSchema = z.object({
    kind: z.enum(PURCHASE_KINDS).default('single'),
    lessons: z.number().int().positive().max(500).default(1),
    /** Total paid, in grosze. */
    totalGrosz: z.number().int().nonnegative(),
    purchasedAt: isoDate,
    note: z.string().max(400).default(''),
});
export const purchaseSchema = purchaseInputSchema.extend({
    id: z.uuid(),
    clientId: z.uuid(),
    createdAt: z.coerce.date(),
});
/** Display name: "Anna Kowalska", or just "Anna" when there is no surname. */
export function clientName(c) {
    return [c.firstName, c.lastName].filter(Boolean).join(' ');
}
